import { MicrobiologyColDef } from './microbiology.component';
import { CultureRow } from './culture-details-ag-renderer.component';
import { DataRevisionIndicator } from '../../shared/data-revision/data-revision-indicator';
import { CultureDetailsAgRendererComponent } from './culture-details-ag-renderer.component';
import { FilterGridColDef } from '../../covance-filter/covance-filter.models';
import { MicrobiologyGridRowItem } from './microbiology-grid-row-item';
import { formatters } from '../../core/formatters';
import { Injectable } from '@angular/core';
import { MultilineFormatter } from '../../shared/multiline-formatter';
import { chain, isNil, isEmpty } from 'lodash';
import { StandardTestResultHelpers } from '../standard-test-results/standard-test-result-helpers';
import { MicrobiologyData } from './microbiology-data';

@Injectable()
export class MicrobiologyColumns {
    public columns: MicrobiologyColDef[] = [
        {

            hide: true,
            headerName: 'Subject',
            field: 'subject',
            colId: 'subject',
            rowGroupIndex: 0,
            hasDataRevision: (row: CultureRow) => row.hasSubjectDataRevision,
            getDataRevisionMessage: (dataRevisionIndicator: DataRevisionIndicator) => dataRevisionIndicator
                .subjectDataRevisionMessage,
        },
        {
            hide: true,
            headerName: 'Visit',
            field: 'visitDescription',
            colId: 'visitDescription',
            rowGroupIndex: 1,
            hasDataRevision: (row: CultureRow) => row.hasVisitDataRevision,
            getDataRevisionMessage: (dataRevisionIndicator: DataRevisionIndicator) => dataRevisionIndicator
                .visitDataRevisionMessage
        },
        {
            hide: true,
            headerName: 'Test Group',
            field: 'testGroupName',
            colId: 'testGroupName',
            isCancelled: (row: CultureRow) =>
                row.isCancelled
                || (row.growthDetails && row.growthDetails.agents && row.growthDetails.agents.some(x => x.isCancelled))
                || (row.textDetails && row.textDetails.isCancelled),
            rowGroupIndex: 2
        },
        {
            hide: true,
            headerName: 'Specimen',
            field: 'specimen',
            colId: 'specimen',
            rowGroupIndex: 3,
            hasDataRevision: (row: CultureRow) => row.hasSpecimenDataRevision,
            getDataRevisionMessage: (dataRevisionIndicator: DataRevisionIndicator) => dataRevisionIndicator
                .resultDataRevisionMessage
        },
        {
            headerName: 'Details',
            field: 'details',
            suppressSorting: true,
            suppressMenu: true,
            getQuickFilterText: this.getTestQuickSearchString,
            cellRendererFramework: CultureDetailsAgRendererComponent,
            getRowHeight: (entity: CultureRow) => {
                // this calculation depends on styles in culture-details-ag-renderer.component.scss
                let size = 0;

                if (entity.growthDetails && entity.growthDetails.agents) {
                    const top = 30;
                    const tableRowSize = 30; // tr height + border 1px
                    const tableHeaderRows = 2;
                    const tableHeaderHeight = 29; //tr height
                    size = top + (entity.growthDetails.agents.length || 1) * tableRowSize + tableHeaderRows * tableHeaderHeight;
                }

                if (entity.textDetails && entity.textDetails.value) {
                    const tableRowSize = 23;
                    const strings = this.multilineFormatter.format(entity.textDetails.value);
                    size = strings.length * tableRowSize;
                }

                return size;
            },
            getLineCount: (entity: CultureRow) => {
                let rows = 0;

                if (entity.textDetails) {
                    const valueRows = this.multilineFormatter.format(entity.textDetails.value).length;
                    if (valueRows > rows) {
                        rows = valueRows;
                    }
                }

                return rows;
            },
            minWidth: 900
        }
    ];

    public exportColumns: FilterGridColDef[] = [
        
        {
            headerName: 'Subject',
            field: 'initials',
            valueGetter: (params: MicrobiologyGridRowItem) => this.getSubjectNumberAndInitials(params)
        },
        {
            headerName: 'Screening Number',
            field: 'screeningNumber',
            valueGetter: (params: MicrobiologyGridRowItem) => this.getScreeningNumber(params)
        },
        {
            headerName: 'Age',
            field: 'age'
        },
        {
            headerName: 'Age Unit',
            field: 'ageUnit'
        },
        {
            headerName: 'Gender',
            field: 'sex'
        },
        {
            headerName: 'Visit Description',
            field: 'visitDescription'
        },
        {
            headerName: 'Collected Time',
            field: 'time',
            valueGetter: (params: MicrobiologyGridRowItem) => this.getCollectedTime(params)
        },
        {
            headerName: 'Accession',
            field: 'accessionNumber'
        },
        {
            headerName: 'Test Group',
            field: 'testGroupName'
        },
        {
            headerName: 'Specimen',
            field: 'specimen'
        },
        {
            headerName: 'Organism',
            field: 'organism',
            valueGetter: (params: MicrobiologyGridRowItem) => this.getOrganismTitle(params)
        },
        {
            headerName: 'Growth',
            field: 'growth'
        },
        {
            headerName: 'Cancellation Reason',
            field: 'cancellationReason'
        },
        {
            headerName: 'Test Description',
            field: 'testDescription'
        },
        {
            headerName: 'Test Result',
            field: 'result',
            valueGetter: (params: MicrobiologyGridRowItem) => {
                if (!params || !params.data || !params.data.result) {
                    return null;
                }

                return params.data.isSubCancelled
                    ? `Cancellation Reason: ${params.data.subCancellationReason}`
                    : params.data.result && params.data.result.replace(/[\r\n]+/g, '; ');
            }
        },
        {
            headerName: 'Drug Name/Agent',
            field: 'agent'
        },
        {
            headerName: 'MIC Result',
            field: 'micResult'
        },
        {
            headerName: 'Units',
            field: 'units'
        },
        {
            headerName: 'MIC Interpretation',
            field: 'micPanelInterpretation'
        },
        {
            headerName: 'Disk Diffusion Result',
            field: 'kirbyBauerPanelZone'
        },
        {
            headerName: 'Disk Diffusion Interpretation',
            field: 'kirbyBauerInterpretation'
        },
        {
            headerName: 'Subject Data Revision',
            field: 'hasSubjectDataRevision',
            valueGetter: (params: MicrobiologyGridRowItem) => this.helper.getDataRevision(params, (micro: MicrobiologyData) => micro.hasSubjectDataRevision)
        },
        {
            headerName: 'Visit Data Revision',
            field: 'hasVisitDataRevision',
            valueGetter: (params: MicrobiologyGridRowItem) => this.helper.getDataRevision(params, (micro: MicrobiologyData) => micro.hasVisitDataRevision)
        },
        {
            headerName: 'Result Data Revision',
            field: 'hasTestResultDataRevision',
            valueGetter: (params: MicrobiologyGridRowItem) => this.helper.getDataRevision(params, (micro: MicrobiologyData) => micro.hasTestResultDataRevision)
        },
    ];

    public get groupedFields() {
        const result = chain(this.columns).filter(c => !isNil(c.rowGroupIndex)).orderBy(c => c.rowGroupIndex)
            .map(c => c.field).value();
        return result;
    }

    constructor(private multilineFormatter: MultilineFormatter, private helper: StandardTestResultHelpers) {

    }

    private getSubjectNumberAndInitials(params: MicrobiologyGridRowItem): string {
        if (!params || !params.data) {
            return null;
        }
        const data = params.data;
        return `${data.subjectNumber} - ${data.initials}`;
    }

    private getScreeningNumber(params: MicrobiologyGridRowItem): string {
        if (!params || !params.data) {
            return null;
        }

        return params.data.screeningNumber;
    }

    private getCollectedTime(params: MicrobiologyGridRowItem): string {
        if (!params || !params.data) {
            return null;
        }
        return formatters.formatDateTime(params.data.collectionDateTime);
    }

    private getOrganismTitle(row: MicrobiologyGridRowItem) {
        if (!row || !row.data) {
            return null;
        }

        return row.data.fullOrganismTitle;
    }

    private getTestQuickSearchString(row: { data: CultureRow }) {
        if (!row || !row.data) {
            return null;
        }
        let resultString = '';
        if (row.data.textDetails) {
            resultString += '|' + row.data.textDetails.title + '|' + row.data.textDetails.value;
        }
        if (row.data.growthDetails) {
            resultString += row.data.growthDetails.organism;
            if (!isEmpty(row.data.growthDetails.agents)) {
                resultString += '|' + row.data.growthDetails.agents
                    .map(a =>
                        a.agent + a.units +
                        a.micPanelInterpretation +
                        a.kirbyBauerInterpretation +
                        a.result + a.micResult)
                    .join('|');
            }
        }
        return resultString;
    }
}